"use-client";

import React, { useEffect, useMemo, useState } from "react";
import Image from "next/image";
import { CircleChevronRight } from "lucide-react";
import placeHolderImage from "@/assets/images/placeholder-img.png";
import { DISPLAY_TYPE } from ".";
import { UploadFile } from "@/types/generated";

interface ISearchHotelResultProps {
  data: {
    name: string;
    img?: UploadFile;
    area_name: string;
    externalUrl: string;
    area_item_name: string;
    item_name: string;
    slug: string;
  }[];
  locale: string;
  showDestinationLabel: string;
  onChangeDisplayType: (type: DISPLAY_TYPE.DESTINATION) => void;
}

const getSecureRandomInt = (max: number) => {
  const array = new Uint32Array(1);
  globalThis.crypto.getRandomValues(array);
  return array[0] % max;
};

export function SearchHotelResult(props: Readonly<ISearchHotelResultProps>) {
  const { data, locale, onChangeDisplayType, showDestinationLabel } = props;
  const [imgSrcList, setImgSrcList] = useState<(UploadFile | undefined)[]>([]);

  const handleNavigate = (slug: string, externalUrl: string = "") => {
    if (externalUrl) {
      globalThis.open(externalUrl, "_blank");
    } else {
      globalThis.location.assign(`/${locale}/${slug}`);
    }
  };

  const [featuredHotels, setFeaturedHotels] = useState<
    ISearchHotelResultProps["data"]
  >([]);

  useEffect(() => {
    // Compute random 3 hotels in an effect (impure Math.random is allowed in effects).
    if (!data || data.length === 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setFeaturedHotels([]);
      return;
    }

    // Fisher-Yates shuffle to avoid using Math.random during render
    const shuffled = [...data];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = getSecureRandomInt(i + 1);
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    setFeaturedHotels(shuffled.slice(0, 3));
  }, [data]);

  const otherHotels = useMemo(() => {
    return data.filter((hotel) => !featuredHotels.includes(hotel));
  }, [data, featuredHotels]);

  useEffect(() => {
    // Initialize image sources from featuredHotels
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setImgSrcList(featuredHotels.map((hotel) => hotel.img));
  }, [featuredHotels]);

  const handleImgError = (_index: number) => {
    setImgSrcList((prev) => {
      const newList = [...prev];
      return newList;
    });
  };

  return (
    <div>
      <div className="custom-scrollbar mt-6 mb-8 max-h-[440px] overflow-y-auto">
        <div>
          {featuredHotels.map((item, index) => {
            const featureHotelKey = `feature-hotel-${index}`;
            return (
              <button
                type="button"
                onClick={() => handleNavigate(item.slug, item.externalUrl)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleNavigate(item.slug, item.externalUrl);
                  }
                }}
                key={featureHotelKey}
                className={`mb-4 flex min-h-[50px] w-full cursor-pointer items-center gap-5 border-y border-[var(--color-gray-200)] ${
                  index === 0 && "md:bg-[var(--color-orange-200)]"
                } bg-transparent p-0 text-left hover:bg-[var(--color-orange-200)]`}
              >
                <div className="flex-shrink-0">
                  <Image
                    src={
                      imgSrcList[index]?.cloudflare_url ||
                      imgSrcList[index]?.url ||
                      placeHolderImage
                    }
                    alt={imgSrcList[index]?.alternativeText || ""}
                    aria-label={imgSrcList[index]?.caption || ""}
                    height={50}
                    width={80}
                    className="h-[50px] w-[80px] object-cover"
                    loading="lazy"
                    onError={() => handleImgError(index)}
                  />
                </div>
                <div className="flex min-h-[50px] flex-1 items-center py-2">
                  <p className="text-sm leading-relaxed font-[var(--font-semibold)] text-[var(--text-gray-light)]">
                    {item.name}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
        <div className="mt-5 grid grid-cols-1 gap-1 text-sm text-gray-700 md:grid-cols-3">
          {otherHotels.map((item, index) => {
            const otherHotelKey = `other-hotel-${index}`;
            return (
              <button
                onClick={() => handleNavigate(item.slug, item.externalUrl)}
                className="flex cursor-pointer text-left py-2 text-sm font-[var(--font-semibold)] text-[var(--text-gray-light)] hover:bg-[var(--color-orange-200)] md:pl-5"
                key={otherHotelKey}
              >
                {item.name}
              </button>
            );
          })}
        </div>
      </div>
      <button
        onClick={() => onChangeDisplayType(DISPLAY_TYPE.DESTINATION)}
        className="flex cursor-pointer items-center gap-3 text-sm font-[var(--font-bold)] text-[var(--color-primary-950)] hover:opacity-80"
      >
        {showDestinationLabel}
        <CircleChevronRight
          size={20}
          fill="var(--color-primary-950)"
          color="#fff"
          strokeWidth={1}
        />
      </button>
    </div>
  );
}
